import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    FormGroup,
    InputAdornment,
    InputLabel,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useStyles } from "./RCALibraryFilters.styles";
import RCAExecutiveMillFilter from "pages/Corporate/RCA/components/RCAExecutiveMillFilter";
import { allElements } from "constants/constants";
import { useCallback, useContext, useEffect, useState } from "react";
import { IRCALibraryFilters } from "./RCALibraryFiltersInterfaces";
import { useRCAMainStyles } from "pages/Corporate/RCA/RCA.styles";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import DateRange from "components/DateRange/DateRange.view";
import useExtraFilters from "./useExtraFilters";
import RCAUsersDropdown from "pages/Corporate/RCA/components/RCAUsersDropdown";
import NewMainSelect from "components/NewMainSelect/NewMainSelect.view";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import useRolesPermissions from "hooks/useRolesPermissions";
import SearchIcon from "@mui/icons-material/Search";
import RCAMeetingTypesDropdown from "pages/Corporate/RCA/components/RCAMeetingTypesDropdown/RCAMeetingTypesDropdown";
import {
    initialRCALibraryExtraFilters,
    RCALibraryFiltersContext,
} from "contexts/RCALibraryFiltersContext/RCALibraryFiltersContext";
import { validRCALibraryExtraFiltersDefaultFields } from "contexts/RCALibraryFiltersContext/RCALibraryFiltersContextProvider";
import DefaultFiltersButton from "pages/Corporate/RCA/components/DefaultFiltersButton/DefaultFiltersButton";
import { objectComparator } from "utilities/utilities";

const RCALibraryFilters = ({ onClickAddNewRCA }: IRCALibraryFilters) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [showExtraFilters, setShowExtraFilters] = useState(false);
    const {
        filters: filtersSelected,
        handleFiltersChange: onSetFilters,
        extraFilters,
        handleExtraFiltersChange,
        handleSetFiltersDefault,
        handleRemoveDefaultFilters,
        userFiltersDefault,
        loadingDefaultOperations,
        isFiltersDefault,
    } = useContext(RCALibraryFiltersContext);
    const [meetingTypeIds, setMeetingTypeIds] = useState<(string | number)[] | null>(null);

    const {
        ownersList,
        shiftsList,
        failureTypesList,
        functionalLocationsList,
        keySuccessFactorsList,
        machineCentersList,
        productionLinesList,
    } = useExtraFilters({ filtersSelected, extraFiltersSelected: extraFilters });
    const mainClasses = useRCAMainStyles();

    const [seletedDateRange, setSelectedDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
        startDate: null,
        endDate: null,
    });

    const onMillComponentLoaded = useCallback(
        (currentUserMillIds: number[]) => {
            if ((filtersSelected.millsIds as number[]).includes(0)) {
                onSetFilters({
                    ...filtersSelected,
                    millsIds: [...currentUserMillIds],
                });
            }
        },
        [filtersSelected, onSetFilters],
    );

    useEffect(() => {
        if (!meetingTypeIds) return;
        if (
            (filtersSelected.meetingTypeIds as number[]).includes(0) &&
            !(filtersSelected.millsIds as number[]).includes(0)
        ) {
            onSetFilters({
                ...filtersSelected,
                meetingTypeIds: meetingTypeIds,
                totalMeetingTypes: meetingTypeIds.length,
            });
        }
    }, [filtersSelected, onSetFilters, meetingTypeIds]);

    const { RCA_PERMISSIONS } = useRolesPermissions();

    const isExtraFiltersApplied = filtersSelected?.productionLineId
        ? !objectComparator(initialRCALibraryExtraFilters, filtersSelected, validRCALibraryExtraFiltersDefaultFields)
        : false;

    return (
        <section className={classes.rcaLibraryMainWrapper}>
            <article className={classes.rcaLibraryFiltersMainWrapper}>
                <div className={`${mainClasses.contentHeaderActions} ${classes.mainFilters}`}>
                    <span className={`${classes.title} ${mainClasses.contentTitle}`}>{t("rca.rca_library.title")}</span>
                    <div className={classes.showCompletedCheck}>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filtersSelected.showCompleted}
                                        value={filtersSelected.showCompleted}
                                        color="info"
                                        onChange={(_, checked) => {
                                            onSetFilters({
                                                ...filtersSelected,
                                                ...(!checked && { statusId: allElements }),
                                                showCompleted: checked,
                                            });
                                        }}
                                    />
                                }
                                label={t("rca.rca_library.rca_library_filters.show_completed")}
                            />
                        </FormGroup>
                    </div>

                    <RCAExecutiveMillFilter
                        onComponentLoaded={onMillComponentLoaded}
                        selectedMillsIds={filtersSelected.millsIds}
                        onMillSelectedChange={(values) => {
                            onSetFilters({
                                ...filtersSelected,
                                ...initialRCALibraryExtraFilters,
                                millsIds: values,
                            });
                            handleExtraFiltersChange(initialRCALibraryExtraFilters);
                        }}
                        onSetExecutiveMillFilters={(filters) => {
                            onSetFilters({
                                ...filtersSelected,
                                ...initialRCALibraryExtraFilters,
                                millsIds: filters.millsIds,
                            });
                            handleExtraFiltersChange(initialRCALibraryExtraFilters);
                        }}
                    />

                    <Box>
                        <InputLabel>
                            <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                {t("rca.rca_library.rca_detail.meeting_type")}
                            </Typography>
                        </InputLabel>
                        <RCAMeetingTypesDropdown
                            notSelectedOption
                            multiple
                            value={filtersSelected.meetingTypeIds}
                            handleOnComponentMount={(meetingTypeIds) => {
                                setMeetingTypeIds(meetingTypeIds);
                            }}
                            onChange={(values) => {
                                onSetFilters({
                                    ...filtersSelected,
                                    meetingTypeIds: values,
                                });
                            }}
                        />
                    </Box>

                    {isExtraFiltersApplied && !showExtraFilters ? (
                        <Tooltip
                            placement="top"
                            PopperProps={{
                                className: mainClasses.tooltip,
                            }}
                            title={
                                <Typography className={mainClasses.tooltipTexts}>
                                    {t("locations.mill_dashboards.filters_applied")}
                                </Typography>
                            }
                        >
                            <ToggleButtonGroup
                                color="info"
                                className={`${mainClasses.contentToggleButton} ${mainClasses.filtersAppliedHidden}`}
                                value={!showExtraFilters && "filter"}
                                exclusive
                                onChange={() => setShowExtraFilters(!showExtraFilters)}
                            >
                                <ToggleButton value="filter">
                                    <FilterAltOutlinedIcon />
                                    {showExtraFilters
                                        ? t("rca.rca_library.hide_filters")
                                        : t("rca.rca_library.show_filters")}
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Tooltip>
                    ) : (
                        <ToggleButtonGroup
                            color="info"
                            className={mainClasses.contentToggleButton}
                            value={!showExtraFilters && "filter"}
                            exclusive
                            onChange={() => setShowExtraFilters(!showExtraFilters)}
                        >
                            <ToggleButton value="filter">
                                <FilterAltOutlinedIcon />
                                {showExtraFilters
                                    ? t("rca.rca_library.hide_filters")
                                    : t("rca.rca_library.show_filters")}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}

                    {!showExtraFilters && (
                        <DefaultFiltersButton
                            variant="contained"
                            color="info"
                            className={mainClasses.contentButton}
                            loading={loadingDefaultOperations}
                            isDefault={isFiltersDefault}
                            hasUserDefault={!!userFiltersDefault}
                            onRemoveDefault={handleRemoveDefaultFilters}
                            onSaveOrUpdate={() => handleSetFiltersDefault({ filters: filtersSelected, extraFilters })}
                        />
                    )}

                    <Button
                        disabled={!RCA_PERMISSIONS?.rca_library.canCreate}
                        variant="contained"
                        color="info"
                        className={mainClasses.contentButton}
                        onClick={onClickAddNewRCA}
                    >
                        <AddCircleIcon />
                        {t("rca.rca_library.add_rca_button")}
                    </Button>
                </div>
                {!!showExtraFilters && (
                    <aside className={classes.extraFilters}>
                        <div className={classes.extraFiltersContent}>
                            <Box className={`${mainClasses.contentFormBox} ${mainClasses.noOverflow}`}>
                                <InputLabel>
                                    <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                        {t("rca.rca_library.rca_library_filters.owner")}
                                    </Typography>
                                </InputLabel>
                                <RCAUsersDropdown
                                    fullWidth
                                    multiple
                                    onChange={(values) => {
                                        handleExtraFiltersChange({ ...extraFilters, ownerId: values });
                                    }}
                                    value={extraFilters.ownerId}
                                    customOptions={ownersList}
                                />
                            </Box>
                            <Box className={`${mainClasses.contentFormBox} ${mainClasses.noOverflow}`}>
                                <InputLabel>
                                    <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                        {t("rca.rca_library.rca_library_filters.shift")}
                                    </Typography>
                                </InputLabel>

                                <>
                                    <NewMainSelect
                                        multiple
                                        fullWidth
                                        order={"asc"}
                                        options={shiftsList}
                                        placeholder={t("rca.rca_library.rca_library_filters.select_shift")}
                                        withSearchBar
                                        onChange={(value) => {
                                            handleExtraFiltersChange({ ...extraFilters, shiftId: value });
                                        }}
                                        value={extraFilters.shiftId}
                                    />
                                </>
                            </Box>
                            <Box className={`${mainClasses.contentFormBox} ${mainClasses.noOverflow}`}>
                                <InputLabel>
                                    <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                        {t("rca.rca_library.rca_library_filters.date_range")}
                                    </Typography>
                                </InputLabel>

                                <>
                                    <DateRange
                                        className={mainClasses.contentDateRangePicker}
                                        placeholderText={t("rca.rca_library.rca_library_filters.select_date_range")}
                                        startDate={seletedDateRange.startDate}
                                        endDate={seletedDateRange.endDate}
                                        hideTooltip
                                        onChange={(update) => {
                                            const [start, end] = update as [Date, Date];
                                            setSelectedDateRange({ startDate: start, endDate: end });

                                            if (start && end) {
                                                handleExtraFiltersChange({
                                                    ...extraFilters,
                                                    startDate: start,
                                                    endDate: end,
                                                });
                                            }
                                        }}
                                    />
                                </>
                            </Box>
                            <Box className={`${mainClasses.contentFormBox} ${mainClasses.noOverflow}`}>
                                <InputLabel>
                                    <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                        {t("rca.rca_library.rca_library_filters.production_line")}
                                    </Typography>
                                </InputLabel>

                                <>
                                    <NewMainSelect
                                        multiple
                                        fullWidth
                                        withSearchBar
                                        order={"asc"}
                                        placeholder={t("rca.rca_library.rca_library_filters.select_production_line")}
                                        options={productionLinesList}
                                        onChange={(value) => {
                                            handleExtraFiltersChange({
                                                ...extraFilters,
                                                productionLineId: value,
                                                machineCenterId: [],
                                                functionalLocationId: [],
                                            });
                                        }}
                                        value={extraFilters.productionLineId}
                                    />
                                </>
                            </Box>
                            <Box className={`${mainClasses.contentFormBox} ${mainClasses.noOverflow}`}>
                                <InputLabel>
                                    <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                        {t("rca.rca_library.rca_library_filters.machine_center")}
                                    </Typography>
                                </InputLabel>

                                <>
                                    <NewMainSelect
                                        multiple
                                        fullWidth
                                        order={"asc"}
                                        placeholder={t("rca.rca_library.rca_library_filters.select_machine_center")}
                                        withSearchBar
                                        options={machineCentersList}
                                        onChange={(value) => {
                                            handleExtraFiltersChange({
                                                ...extraFilters,
                                                machineCenterId: value,
                                                functionalLocationId: [],
                                            });
                                        }}
                                        value={extraFilters.machineCenterId}
                                    />
                                </>
                            </Box>
                            <Box className={`${mainClasses.contentFormBox} ${mainClasses.noOverflow}`}>
                                <InputLabel>
                                    <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                        {t("rca.rca_library.rca_library_filters.functional_location")}
                                    </Typography>
                                </InputLabel>

                                <>
                                    <NewMainSelect
                                        multiple
                                        fullWidth
                                        withSearchBar
                                        order={"asc"}
                                        placeholder={t(
                                            "rca.rca_library.rca_library_filters.select_functional_location",
                                        )}
                                        options={functionalLocationsList}
                                        onChange={(value) => {
                                            handleExtraFiltersChange({ ...extraFilters, functionalLocationId: value });
                                        }}
                                        value={extraFilters.functionalLocationId}
                                    />
                                </>
                            </Box>
                            <Box className={`${mainClasses.contentFormBox} ${mainClasses.noOverflow}`}>
                                <InputLabel>
                                    <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                        {t("rca.rca_library.rca_library_filters.key_success_factor")}
                                    </Typography>
                                </InputLabel>

                                <>
                                    <NewMainSelect
                                        multiple
                                        fullWidth
                                        withSearchBar
                                        order={"asc"}
                                        placeholder={t("rca.rca_library.rca_library_filters.select_key_success_factor")}
                                        options={keySuccessFactorsList}
                                        onChange={(value) => {
                                            handleExtraFiltersChange({ ...extraFilters, keySuccessFactorId: value });
                                        }}
                                        value={extraFilters.keySuccessFactorId}
                                    />
                                </>
                            </Box>
                            <Box className={`${mainClasses.contentFormBox} ${mainClasses.noOverflow}`}>
                                <InputLabel>
                                    <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                        {t("rca.rca_library.rca_library_filters.failure_types")}
                                    </Typography>
                                </InputLabel>

                                <>
                                    <NewMainSelect
                                        multiple
                                        fullWidth
                                        withSearchBar
                                        order={"asc"}
                                        placeholder={t("rca.rca_library.rca_library_filters.select_failure_types")}
                                        options={failureTypesList}
                                        onChange={(value) => {
                                            handleExtraFiltersChange({ ...extraFilters, failureTypeId: value });
                                        }}
                                        value={extraFilters.failureTypeId}
                                    />
                                </>
                            </Box>
                        </div>
                        <Divider />
                        <div className={classes.extraFiltersActions}>
                            <DefaultFiltersButton
                                variant="text"
                                color="info"
                                className={mainClasses.contentButton}
                                loading={loadingDefaultOperations}
                                isDefault={isFiltersDefault}
                                hasUserDefault={!!userFiltersDefault}
                                onRemoveDefault={() => {
                                    handleRemoveDefaultFilters();
                                    handleExtraFiltersChange(initialRCALibraryExtraFilters);
                                    setSelectedDateRange({ startDate: null, endDate: null });
                                    onSetFilters({
                                        ...filtersSelected,
                                        ...initialRCALibraryExtraFilters,
                                    });
                                }}
                                onSaveOrUpdate={() => {
                                    handleSetFiltersDefault({ filters: filtersSelected, extraFilters });
                                    onSetFilters({
                                        ...filtersSelected,
                                        ...extraFilters,
                                    });
                                }}
                            />
                            <Button
                                variant="text"
                                color="info"
                                className={mainClasses.contentButton}
                                onClick={() => {
                                    handleExtraFiltersChange(initialRCALibraryExtraFilters);
                                    setSelectedDateRange({ startDate: null, endDate: null });
                                    onSetFilters({
                                        ...filtersSelected,
                                        ...initialRCALibraryExtraFilters,
                                    });
                                }}
                            >
                                {t("rca.rca_library.rca_library_filters.clear_filters")}
                            </Button>
                            <Button
                                variant="contained"
                                color="info"
                                className={mainClasses.contentButton}
                                type="submit"
                                onClick={() => {
                                    onSetFilters({
                                        ...filtersSelected,
                                        ...extraFilters,
                                    });
                                }}
                            >
                                {t("rca.rca_library.rca_library_filters.apply_filters")}
                            </Button>
                        </div>
                    </aside>
                )}
            </article>
            <article className={classes.searchBar}>
                <TextField
                    fullWidth
                    className={`${mainClasses.contentFormMultitextField}`}
                    name="description"
                    type="text"
                    variant="outlined"
                    value={extraFilters.searchText}
                    placeholder={t("rca.rca_library.rca_library_filters.search_keyword")}
                    onChange={(event) => {
                        handleExtraFiltersChange({ ...extraFilters, searchText: event.target.value });
                        onSetFilters({
                            ...filtersSelected,
                            ...extraFilters,
                            searchText: event.target.value,
                        });
                    }}
                    size="small"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </article>
        </section>
    );
};

export default RCALibraryFilters;
